interface Account {
    prefix?: string;
    number?: string;
    bankCode?: string;
    iban?: string;
    countryCode?: string;
}

interface OutputValues {
    iban?: string;
    number?: string;
}

const RE_EMPTY_PREFIX = /^[0]*$/gi;
const BANK_CODE_SEPARATOR = '/';
const PREFIX_SEPARATOR = '-';
const RE_HU_ACCOUNT_NUMBER_CHUNKS = /.{1,8}/g;
const HU_ACCOUNT_NUMBER_SEPARATOR = '-';
const RE_RS_ACCOUNT_NUMBER_CHUNKS = /^(\d{3})(\d{13})(\d{2})/;
const RS_ACCOUNT_NUMBER_SEPARATOR = ' ';

export function getFormattedPrefix(prefix?: string): string {
    if (!prefix) {
        return '';
    }

    if (prefix.match(RE_EMPTY_PREFIX)) {
        return '';
    }

    return `${prefix}${PREFIX_SEPARATOR}`;
}

export function getFormattedBankCode(bankCode?: string): string {
    if (!bankCode) {
        return '';
    }

    return `${BANK_CODE_SEPARATOR}${bankCode}`;
}

export function getFormattedAccount(account: Account): OutputValues {
    const { iban, number, bankCode } = account;
    if (iban) {
        return {
            iban,
        };
    }

    if (number) {
        const formattedBankCode = getFormattedBankCode(bankCode);

        return {
            number: `${number}${formattedBankCode}`,
        };
    }

    return {};
}

export function getCZFormattedAccount(account: Account): OutputValues {
    const { prefix, number, bankCode, iban, countryCode } = account;

    if (number && countryCode === 'CZ') {
        const result: OutputValues = {};
        const formattedPrefix = getFormattedPrefix(prefix);
        const formattedBankCode = getFormattedBankCode(bankCode);
        result.number = `${formattedPrefix}${number}${formattedBankCode}`;

        if (iban) {
            result.iban = iban;
        }

        return result;
    }

    return getFormattedAccount(account);
}

function formatHUAccountNumber(value: string): string {
    const chunks = value.match(RE_HU_ACCOUNT_NUMBER_CHUNKS) || [];

    if (chunks.length === 0) {
        return value;
    }

    return chunks.join(HU_ACCOUNT_NUMBER_SEPARATOR);
}

export function getHUFormattedAccount(account: Account): OutputValues {
    const { number, iban, countryCode } = account;

    if (number && countryCode === 'HU') {
        const result: OutputValues = {};
        result.number = formatHUAccountNumber(number);

        if (iban) {
            result.iban = iban;
        }

        return result;
    }

    return getFormattedAccount(account);
}

function formatRSAccountNumber(value: string): string {
    const [, ...chunks] = value.match(RE_RS_ACCOUNT_NUMBER_CHUNKS) || [];

    if (chunks.length === 0) {
        return value;
    }

    return chunks.join(RS_ACCOUNT_NUMBER_SEPARATOR);
}

export function getRSFormattedAccount(account: Account): OutputValues {
    const { number, iban, countryCode } = account;

    if (number && countryCode === 'RS') {
        const result: OutputValues = {};
        result.number = formatRSAccountNumber(number);

        if (iban) {
            result.iban = iban;
        }

        return result;
    }

    return getFormattedAccount(account);
}
